import { useState, useEffect } from 'react'
import { workspaceService, projectService, taskService } from '@/services'
import { toast } from 'sonner'
import type { Workspace, Project, Task, User } from '@/types'

interface UseWorkspaceDataOptions {
  userId?: string
  workspaceId?: string
}

export function useWorkspaceData({ userId, workspaceId }: UseWorkspaceDataOptions) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [users, setUsers] = useState<User[]>([])

  // Load workspaces on mount or when userId changes
  useEffect(() => {
    if (userId) {
      loadWorkspaces()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  // Load workspace-specific data when workspaceId changes
  useEffect(() => {
    if (workspaceId) {
      loadProjectsForWorkspace(workspaceId)
      loadTasksForWorkspace(workspaceId)
      loadUsersForWorkspace(workspaceId)
    } else {
      // Reset data when no workspace is selected
      setProjects([])
      setTasks([])
      setUsers([])
    }
  }, [workspaceId])

  const loadWorkspaces = async () => {
    if (!userId) return

    try {
      const allWorkspaces = await workspaceService.getAll()

      // Filter workspaces where user is creator or member
      const userWorkspaces = allWorkspaces.filter(
        (workspace) =>
          workspace.creator.id === userId || workspace.users.some((u) => u.id === userId)
      )

      setWorkspaces(userWorkspaces)
    } catch (error) {
      console.error('Failed to load workspaces:', error)
      toast.error('Failed to load workspaces')
    }
  }

  const loadProjectsForWorkspace = async (wsId: string) => {
    try {
      const workspaceProjects = await projectService.getProjectsForWorkspace(wsId)
      setProjects(workspaceProjects)
    } catch (error) {
      console.error('Failed to load projects:', error)
      setProjects([])
    }
  }

  const loadTasksForWorkspace = async (wsId: string) => {
    try {
      const allTasks = await taskService.getAll()
      const workspaceTasks = allTasks.filter((task) => task.workspace.id === wsId)
      setTasks(workspaceTasks)
    } catch (error) {
      console.error('Failed to load tasks:', error)
      setTasks([])
    }
  }

  const loadUsersForWorkspace = async (wsId: string) => {
    try {
      const workspace = await workspaceService.getById(wsId)
      if (workspace) {
        setUsers(workspace.users)
      }
    } catch (error) {
      console.error('Failed to load users:', error)
      setUsers([])
    }
  }

  return {
    workspaces,
    projects,
    tasks,
    users,
  }
}
